import { memo, useEffect, useRef } from 'react';
import { createChart, CrosshairMode } from 'lightweight-charts';
import moment from 'moment';
import { calculateMovingAverageSeriesData } from '../../controllers/analysis';
import { CHART_OPEN_MODE } from '../../utils/global_variables';
import { useSelector } from 'react-redux';

const LineChart = (props) => {
    const { lineData = [], isMa = null, isExpand, mode = CHART_OPEN_MODE.OVERVIEW, halfHeight } = props;
    const isSideExpanded = useSelector((state) => state.marketOverview.isSideExpanded);
    const chartContainerRef = useRef();
    const chart = useRef();

    useEffect(() => {
        if (lineData?.length > 0) {
            if (chart.current) {
                chart.current.remove();
            }

            // Remove old tooltip if it exists
            const oldTooltip = document.getElementById('tooltip');
            if (oldTooltip) {
                chartContainerRef.current.removeChild(oldTooltip);
            }

            chart.current = createChart(chartContainerRef.current, {
                crosshair: { mode: CrosshairMode.Normal },
            });
            chart.current.applyOptions({
                timeScale: { timeVisible: false },
            });

            const ohlcLineSeries = chart.current.addLineSeries({
                color: '#2962FF',
                lastValueVisible: false,
                priceLineVisible: false,
            });
            ohlcLineSeries.setData(lineData);

            const volumeSeries = chart.current.addHistogramSeries({
                priceFormat: { type: 'volume' },
                priceScaleId: '',
                scaleMargins: { top: 0.8, bottom: 0 },
            });

            volumeSeries.setData(
                lineData.map((x) => {
                    const barColor = x?.close >= x?.open ? '#26a69a' : '#ef5350';
                    return {
                        time: x?.time,
                        value: x?.volume || 0,
                        color: barColor,
                    };
                })
            );

            const toolTipWidth = 120;
            const toolTipHeight = 90;
            const toolTipMargin = 15;

            const toolTip = document.createElement('div');
            toolTip.id = 'tooltip';
            toolTip.style = `
                width: ${toolTipWidth}px;
                position: absolute;
                display: none;
                padding: 8px;
                box-sizing: border-box;
                font-size: 12px;
                text-align: left;
                z-index: 1000;
                pointer-events: none;
                border: 1px solid rgba(38,166,154,1);
                border-radius: 4px;
                background: rgba(255,255,255,0.95);
                color: black;
                font-family: -apple-system, BlinkMacSystemFont, 'Trebuchet MS', Roboto, Ubuntu, sans-serif;
            `;
            chartContainerRef.current.appendChild(toolTip);

            chart.current.subscribeCrosshairMove((param) => {
                if (
                    !param.time ||
                    !param.point ||
                    param.point.x < 0 ||
                    param.point.x > chartContainerRef.current.clientWidth ||
                    param.point.y < 0 ||
                    param.point.y > chartContainerRef.current.clientHeight
                ) {
                    toolTip.style.display = 'none';
                    return;
                }

                // Get data for both series
                const priceData = param.seriesData.get(ohlcLineSeries);
                const volumeData = param.seriesData.get(volumeSeries);

                const price = priceData?.value ?? priceData?.close;
                const volume = volumeData?.value ?? 0;

                const timestamp =
                    typeof param.time === 'number'
                        ? param.time
                        : param.time?.timestamp || 0;

                const formattedDate = moment
                    .unix(timestamp)
                    .utc()
                    .format('DD MMM YY, hh:mm A ');

                // Format volume nicely
                const formattedVolume =
                    volume >= 1_000_000
                        ? (volume / 1_000_000).toFixed(2) + 'M'
                        : volume >= 1_000
                        ? (volume / 1_000).toFixed(2) + 'K'
                        : volume.toString();

                const color = priceData?.close >= priceData?.open ? '#26a69a' : '#ef5350';
                toolTip.style.borderColor = color;

                // Tooltip content
                toolTip.style.display = 'block';
                toolTip.innerHTML = `
                    <div style="font-size: 20px; font-weight: bold; color: black; text-align: center;">
                        ${Math.round(100 * price) / 100}
                    </div>
                    <div style="font-size: 12px; color: #555; text-align: center;">
                        ${formattedDate}
                    </div>
                    <div style="font-size: 12px; color: #888; text-align: center;">
                        Vol: ${formattedVolume}
                    </div>
                `;

                // Tooltip position
                const { x, y } = param.point;
                let left = x + toolTipMargin;
                if (left > chartContainerRef.current.clientWidth - toolTipWidth) {
                    left = x - toolTipMargin - toolTipWidth;
                }

                let top = y + toolTipMargin;
                if (top > chartContainerRef.current.clientHeight - toolTipHeight) {
                    top = y - toolTipHeight - toolTipMargin;
                }

                toolTip.style.left = left + 'px';
                toolTip.style.top = top + 'px';
            });

            if (isMa) {
                const maData = calculateMovingAverageSeriesData(lineData, isMa);
                const maSeries = chart.current.addLineSeries({
                    color: '#f76f26',
                    lineWidth: 1,
                });
                maSeries.setData(maData);
            }

            chart.current.timeScale().fitContent();
        }
    }, [isMa, lineData, isExpand, isSideExpanded]);

    return (
        <div data-testid="analysis-tab-technical-line-chart"
            className={
                mode === CHART_OPEN_MODE.OVERVIEW
                    ? 'scripoverview-chart-wrapper'
                    : halfHeight
                    ? 'sidetray-chart-wrapper side-overview-chart-halfheight'
                    : 'sidetray-chart-wrapper'
            }
        >
            <div
                ref={chartContainerRef}
                className={
                    mode === CHART_OPEN_MODE.OVERVIEW
                        ? 'scripoverview-chart'
                        : 'sidetray-chart'
                }
            />
        </div>
    );
};

export default memo(LineChart);
